import { BillingPeriod, Charge, ChargeDetails, ChargePlan, ChargeType, PricePeriod } from '../models/paywall-settings';
import { isQuantityCharge } from './quantity.helper';

export interface PricingContext {
  currencyCode: string;
  billingPeriod: number;
  /** Annual discount in percent, applied to every charge when billing annually. */
  discount?: number | null;
}

export function chargeTypeOf(charge: Charge | undefined): ChargeType {
  return enumValue(charge?.chargeType, ChargeType, ChargeType.Recurring);
}

export function pricePeriodOf(charge: Charge | undefined): PricePeriod {
  return enumValue(charge?.pricePeriod, PricePeriod, PricePeriod.Monthly);
}

export function isOneOffCharge(charge: Charge | undefined): boolean {
  return chargeTypeOf(charge) === ChargeType.OneOff;
}

export function isRecurringCharge(charge: Charge | undefined): boolean {
  return chargeTypeOf(charge) === ChargeType.Recurring;
}

export function recurringCharges(plan: ChargePlan | undefined): Charge[] {
  return (plan?.charges ?? []).filter(charge => !isOneOffCharge(charge));
}

export function oneOffCharges(plan: ChargePlan | undefined): Charge[] {
  return (plan?.charges ?? []).filter(isOneOffCharge);
}

export function hasOneOffCharges(plan: ChargePlan | undefined): boolean {
  return oneOffCharges(plan).length > 0;
}

export function chargeDetailFor(charge: Charge | undefined, currencyCode: string): ChargeDetails | undefined {
  return charge?.chargeDetails?.find(detail => detail.priceCurrencyCode === currencyCode);
}

/** Price of one unit (or the flat price) in the charge's own price period. */
export function chargeUnitPrice(charge: Charge | undefined, currencyCode: string): number {
  return chargeDetailFor(charge, currencyCode)?.price ?? 0;
}

export function billingPeriodMonths(billingPeriod: number): number {
  switch (billingPeriod) {
    case BillingPeriod.Quarterly: return 3;
    case BillingPeriod.Annual: return 12;
    case BillingPeriod.Biannual: return 6;
    default: return 1;
  }
}

export function pricePeriodMonths(pricePeriod: PricePeriod): number {
  switch (pricePeriod) {
    case PricePeriod.Quarterly: return 3;
    case PricePeriod.Annual: return 12;
    default: return 1;
  }
}

export function discountFactor(context: PricingContext): number {
  if (context.billingPeriod !== BillingPeriod.Annual || context.discount == null) {
    return 1;
  }
  return 1 - context.discount / 100;
}

/**
 * Unit price for the selected billing period: recurring prices are scaled from the charge's price
 * period, one-off prices are not. The annual discount applies to every charge, as the order discount
 * the backend creates is not limited to specific charges.
 */
export function chargePeriodUnitPrice(charge: Charge | undefined, context: PricingContext): number {
  const price = chargeUnitPrice(charge, context.currencyCode);
  const scale = isOneOffCharge(charge)
    ? 1
    : billingPeriodMonths(context.billingPeriod) / pricePeriodMonths(pricePeriodOf(charge));
  return price * scale * discountFactor(context);
}

export function chargeAmount(charge: Charge | undefined, quantity: number, context: PricingContext): number {
  const qty = isQuantityCharge(charge) ? quantity : 1;
  return chargePeriodUnitPrice(charge, context) * qty;
}

export function planRecurringTotal(plan: ChargePlan | undefined, quantity: number, context: PricingContext): number {
  return recurringCharges(plan).reduce((sum, charge) => sum + chargeAmount(charge, quantity, context), 0);
}

export function planOneTimeTotal(plan: ChargePlan | undefined, quantity: number, context: PricingContext): number {
  return oneOffCharges(plan).reduce((sum, charge) => sum + chargeAmount(charge, quantity, context), 0);
}

/** Currencies every charge of every plan has a price in, sorted alphabetically. */
export function commonCurrencyCodes(plans: ChargePlan[]): string[] {
  const charges = plans.flatMap(plan => plan.charges ?? []);
  if (charges.length === 0) {
    return [];
  }
  const codeSets = charges.map(charge => new Set((charge.chargeDetails ?? []).map(detail => detail.priceCurrencyCode)));
  return [...codeSets[0]]
    .filter(code => !!code && codeSets.every(codes => codes.has(code)))
    .sort();
}

function enumValue<T extends number>(value: unknown, enumObject: Record<string, string | number>, fallback: T): T {
  if (typeof value === 'number') {
    return value as T;
  }
  if (typeof value === 'string' && value.length > 0) {
    const normalized = value.replace(/[_\s-]/g, '').toLowerCase();
    const key = Object.keys(enumObject).find(name => isNaN(Number(name)) && name.toLowerCase() === normalized);
    if (key) {
      return enumObject[key] as T;
    }
  }
  return fallback;
}
